import React, { useEffect, useRef, useState, useCallback } from 'react';
import './HighlighterDrawOverlay.css';
import { noise1D, pointsToSmoothPath, WIGGLE_AMPLITUDE, WIGGLE_TIME_FREQ, WIGGLE_SPATIAL_FREQ } from '../utils/wiggle';

// Build-time brush enumeration via Webpack require.context (CRA)
const brushContext = require.context('../assets/brushes', false, /\.(png|webp)$/);
const BRUSH_URLS = brushContext.keys().sort().map(brushContext);

const PASTEL_COLORS = [
  '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9',
  '#BAE1FF', '#E8BAFF', '#FFB3DE', '#B3FFE0',
  '#FFDAB3', '#D4BAFF',
];

const FADE_MS = 2500;
const REDUCED_FADE_MS = 300;
const STROKE_WIDTH = 24;
const MIN_POINT_DIST = 4;
const STAMP_SPACING = 8;
const STAMP_SIZE = 100;
const MAX_STAMPS = 600;
const TOUCH_COMMIT_DIST = 12;

// Expand a single point into a closed ring of N points so the wiggle system
// has geometry to perturb into an organic, wobbly blob.
function expandSinglePoint(p, radius) {
  const N = 8;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const angle = (i / N) * Math.PI * 2;
    pts.push({ x: p.x + Math.cos(angle) * radius, y: p.y + Math.sin(angle) * radius });
  }
  return pts;
}

// Walk an array of points and return positions spaced ~spacing px apart
// along the polyline, plus the tangent angle at each position.
function computeStampPositions(pts, spacing) {
  if (pts.length < 2) {
    return pts.length === 1 ? [{ x: pts[0].x, y: pts[0].y, angle: 0 }] : [];
  }
  const positions = [{ x: pts[0].x, y: pts[0].y, angle: 0 }];
  let carry = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    const segLen = Math.sqrt(dx * dx + dy * dy);
    if (segLen === 0) continue;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    let traveled = carry;
    while (traveled + spacing <= segLen) {
      traveled += spacing;
      const t = traveled / segLen;
      positions.push({
        x: pts[i - 1].x + dx * t,
        y: pts[i - 1].y + dy * t,
        angle,
      });
      if (positions.length >= MAX_STAMPS) return positions;
    }
    carry = segLen - traveled;
  }
  return positions;
}

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let nextStrokeId = 0;

// Selector for elements that should receive normal click/hover instead of drawing.
const INTERACTIVE_SELECTOR =
  'a, button, input, textarea, select, .cv, .hobbies, #mybag, .social-link, .inbag, .pen-toggle-btn';

const HighlighterDrawOverlay = ({ enabled, zIndex, penType }) => {
  const strokesRef = useRef([]);
  const activeStrokeRef = useRef(null);
  const colorIndexRef = useRef(0);
  const brushIndexRef = useRef(-1);
  const rafIdRef = useRef(null);
  const [, setTick] = useState(0);
  const forceRender = useCallback(() => setTick(t => t + 1), []);

  const fadeDuration = prefersReducedMotion ? REDUCED_FADE_MS : FADE_MS;

  // Stash latest props in refs so document-level listeners always read current values.
  const enabledRef = useRef(enabled);
  const penTypeRef = useRef(penType);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { penTypeRef.current = penType; }, [penType]);

  const perturbPoints = useCallback((basePoints, phaseSeed, t, opacity) => {
    // For single-point strokes, expand into a ring so wiggle produces a wobbly blob.
    const pts = basePoints.length === 1
      ? expandSinglePoint(basePoints[0], 1)
      : basePoints;
    const amp = WIGGLE_AMPLITUDE * Math.min(opacity, 1) * (basePoints.length === 1 ? 0.5 : 1);
    return pts.map((p, i) => {
      let nx = 0, ny = 0;
      if (pts.length > 1) {
        const prev = pts[Math.max(0, i - 1)];
        const next = pts[Math.min(pts.length - 1, i + 1)];
        const tx = next.x - prev.x;
        const ty = next.y - prev.y;
        const len = Math.sqrt(tx * tx + ty * ty) || 1;
        nx = -ty / len;
        ny = tx / len;
      }
      const n = noise1D(i * WIGGLE_SPATIAL_FREQ + t * WIGGLE_TIME_FREQ + phaseSeed);
      return { x: p.x + nx * n * amp, y: p.y + ny * n * amp };
    });
  }, []);

  const animationLoop = useCallback(() => {
    const now = performance.now();
    const t = now / 1000;
    let needsLoop = false;
    const active = activeStrokeRef.current;
    const all = active ? [active, ...strokesRef.current] : strokesRef.current;

    for (let i = 0; i < all.length; i++) {
      const s = all[i];

      if (s.state === 'fading') {
        const fadeT = Math.min((now - s.fadeStartedAt) / fadeDuration, 1);
        s.opacity = Math.max(0, 1 - fadeT * fadeT * fadeT);
      }

      if (s.penType === 'highlighter') {
        if (!prefersReducedMotion && s.opacity > 0.05) {
          const perturbed = perturbPoints(s.basePoints, s.phaseSeed, t, s.opacity);
          s.pathD = pointsToSmoothPath(perturbed);
          needsLoop = true;
        } else if (!s.pathD || s.state === 'drawing') {
          s.pathD = pointsToSmoothPath(s.basePoints);
        }
      }

      if (s.penType === 'stamp' && s.state === 'drawing') {
        s.stampPositions = computeStampPositions(s.basePoints, s.stampSpacing);
      }

      if (s.opacity > 0) needsLoop = true;
    }

    strokesRef.current = strokesRef.current.filter(s => s.opacity > 0);
    forceRender();

    if (needsLoop) {
      rafIdRef.current = requestAnimationFrame(animationLoop);
    } else {
      rafIdRef.current = null;
    }
  }, [fadeDuration, forceRender, perturbPoints]);

  const ensureLoop = useCallback(() => {
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(animationLoop);
    }
  }, [animationLoop]);

  // Keep ensureLoop / perturbPoints accessible to document-level listeners via ref.
  const ensureLoopRef = useRef(ensureLoop);
  const perturbPointsRef = useRef(perturbPoints);
  useEffect(() => { ensureLoopRef.current = ensureLoop; }, [ensureLoop]);
  useEffect(() => { perturbPointsRef.current = perturbPoints; }, [perturbPoints]);

  // Document-level pointer listeners. The SVG overlay has pointer-events:none,
  // so events reach the real page content. If the target is interactive we do
  // nothing; otherwise we start / continue / finish a drawing stroke.
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!enabledRef.current) return;
      if (e.target.closest(INTERACTIVE_SELECTOR)) return;

      const isTouch = e.pointerType === 'touch';
      if (!isTouch) {
        e.preventDefault();
        e.target.setPointerCapture(e.pointerId);
      }

      let pt = penTypeRef.current;
      if (pt === 'stamp' && BRUSH_URLS.length === 0) pt = 'highlighter';

      const stroke = {
        id: nextStrokeId++,
        penType: pt,
        basePoints: [{ x: e.clientX, y: e.clientY }],
        color: PASTEL_COLORS[colorIndexRef.current % PASTEL_COLORS.length],
        width: STROKE_WIDTH,
        opacity: isTouch ? 0 : 1,
        state: isTouch ? 'pending' : 'drawing',
        phaseSeed: Math.random() * 1000,
        createdAt: performance.now(),
        fadeStartedAt: null,
        pathD: '',
        brushUrl: null,
        stampSpacing: STAMP_SPACING,
        stampSize: STAMP_SIZE,
        stampPositions: [],
        startX: e.clientX,
        startY: e.clientY,
      };

      if (pt === 'highlighter') {
        colorIndexRef.current++;
      } else {
        brushIndexRef.current = (brushIndexRef.current + 1) % BRUSH_URLS.length;
        stroke.brushUrl = BRUSH_URLS[brushIndexRef.current];
        stroke.stampPositions = computeStampPositions(stroke.basePoints, stroke.stampSpacing);
      }

      activeStrokeRef.current = stroke;
      ensureLoopRef.current();
    };

    const onPointerMove = (e) => {
      const stroke = activeStrokeRef.current;
      if (!stroke) return;
      if (e.pointerType !== 'touch') e.preventDefault();

      if (stroke.state === 'pending') {
        const dx = e.clientX - stroke.startX;
        const dy = e.clientY - stroke.startY;
        if (dx * dx + dy * dy >= TOUCH_COMMIT_DIST * TOUCH_COMMIT_DIST) {
          stroke.state = 'drawing';
          stroke.opacity = 1;
        }
      }

      const last = stroke.basePoints[stroke.basePoints.length - 1];
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      if (dx * dx + dy * dy < MIN_POINT_DIST * MIN_POINT_DIST) return;
      stroke.basePoints.push({ x: e.clientX, y: e.clientY });
    };

    const onPointerUp = () => {
      const stroke = activeStrokeRef.current;
      if (!stroke) return;
      if (stroke.state === 'pending') {
        stroke.opacity = 1;
      }
      stroke.state = 'fading';
      stroke.fadeStartedAt = performance.now();

      if (stroke.penType === 'stamp') {
        stroke.stampPositions = computeStampPositions(stroke.basePoints, stroke.stampSpacing);
      }
      if (stroke.penType === 'highlighter') {
        const t = performance.now() / 1000;
        const perturbed = perturbPointsRef.current(stroke.basePoints, stroke.phaseSeed, t, stroke.opacity);
        stroke.pathD = pointsToSmoothPath(perturbed);
      }

      strokesRef.current.push(stroke);
      activeStrokeRef.current = null;
      ensureLoopRef.current();
    };

    const onPointerCancel = () => {
      const stroke = activeStrokeRef.current;
      if (!stroke) return;

      if (stroke.state === 'pending') {
        activeStrokeRef.current = null;
        ensureLoopRef.current();
        return;
      }

      onPointerUp();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  const allStrokes = activeStrokeRef.current
    ? [...strokesRef.current, activeStrokeRef.current]
    : strokesRef.current;

  return (
    <svg
      className="highlighter-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex,
        pointerEvents: 'none',
      }}
    >
      {allStrokes.map(s =>
        s.penType === 'highlighter' ? (
          <path
            key={s.id}
            d={s.pathD}
            stroke={s.color}
            strokeWidth={s.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity={s.opacity}
          />
        ) : (
          <g key={s.id}>
            {(s.stampPositions || []).map((pos, i) => (
              <g
                key={i}
                transform={`translate(${pos.x},${pos.y})`}
                style={{ opacity: s.opacity }}
              >
                <g
                  className={prefersReducedMotion ? undefined : 'brushSpin'}
                  style={
                    prefersReducedMotion
                      ? undefined
                      : {
                          animationDuration: `${fadeDuration}ms`,
                          animationDelay: `${(i % 12) * 12}ms`,
                        }
                  }
                >
                  <image
                    href={s.brushUrl}
                    width={s.stampSize}
                    height={s.stampSize}
                    x={-s.stampSize / 2}
                    y={-s.stampSize / 2}
                  />
                </g>
              </g>
            ))}
          </g>
        )
      )}
    </svg>
  );
};

export default HighlighterDrawOverlay;
